import { useState, useEffect } from "react";
import { useWatch } from "react-hook-form";
import styles from "./index.module.scss";
import TextArea from "../../../TextArea";
import CommentGroup from "../../../ADP/CommentGroup";
import CheckboxGroup from "../../../CheckboxGroup";
import RadioDevice from "../../../RadioDevice";
import RadioGroup from "../../../RadioGroup";
import TextLink from "../../../TextLinks";
import PageMarker from "../../../ADP/PageMarker";
import { Text } from "../../../Type";
import Tooltip from "../../../Tooltip";
import Modal from "../../../Modal";
import Button from "../../../Button";
import TextGrid from "../../../TextGrid";
import UploadDocumentsModal from "../../../ADP/UploadDocumentsModal";
import { reasonOptions } from "../../../../utils/labelMap/LabelMap";
import { nonSpecialCharacterOptions } from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useStoreContext } from "../../../../contexts/StoreProvider";

// #region options

// #endregion

const Section2d = () => {
  const {
    state: { externalUrls },
  } = useStoreContext();
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const { clientFile, formMode: mode } = useClientFileContext();
  const fileStatus = clientFile.status;

  // LOGIC for clinical rationale textbox show/hide
  const customModificationsObject = useWatch({
    name: "section2d.custom-modifications-required",
  });
  const checkCustomModifications = customModificationsObject === "cm-yes";

  // LOGIC for popup for additional documents: https://app.asana.com/0/0/1200354578179107/f
  const [prevReplacementDevices, setPrevReplacementDevice] = useState(
    useWatch({ name: "section2d.replacement-devices" })
  );
  const currReplacementDevice = useWatch({
    name: "section2d.replacement-devices",
  });
  const reasonForApplication2d = useWatch({
    name: "section2d.reason-for-application",
  });

  const [openPreviouslyFundedModal, setOpenPreviouslyFundedModal] =
    useState(false);
  useEffect(() => {
    if (
      Array.isArray(prevReplacementDevices) &&
      Array.isArray(currReplacementDevice) &&
      currReplacementDevice?.includes("2d-special-circumstances") &&
      !prevReplacementDevices?.includes("2d-special-circumstances")
    ) {
      setOpenPreviouslyFundedModal(true);
    }
    setPrevReplacementDevice(currReplacementDevice);
  }, [currReplacementDevice, prevReplacementDevices]);

  const replacementOptions = [
    {
      label:
        "Change in applicant’s mobility status - previously ADP funded equipment no longer meeting basic mobility needs as defined by ADP for funding purposes",
      value: "2d-change-in-status",
    },
    {
      label:
        "Change in applicant’s body size - previously ADP funded equipment is either too large or too small",
      value: "2d-change-in-size",
    },
    {
      label: "Previously ADP funded equipment is worn out",
      value: "2d-worn-out",
    },
    {
      label: "Special circumstances - none of the above",
      value: "2d-special-circumstances",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach letter of rationale.
        </TextLink>
      ),
    },
  ];

  // LOGIC for conditional requiredText in replacement-devices
  const currIsReplacementDevicesRequired =
    reasonForApplication2d !== "first-access" &&
    reasonForApplication2d !== "another-type";
  // Need to use useState + useEffect to trigger re-renders
  const [isReplacementDevicesRequired, setIsReplacementDevicesRequired] =
    useState(currIsReplacementDevicesRequired);
  useEffect(() => {
    setIsReplacementDevicesRequired(currIsReplacementDevicesRequired);
  }, [currIsReplacementDevicesRequired]);

  return (
    <>
      <PageMarker page={9} total={13} />
      <Text className={styles.marginAfterPM} size="xlarge" weight="bold">
        Devices and Options
      </Text>
      <Text className={styles.margin}>
        For each option, either modular or custom fabricated must be selected.{" "}
        <Tooltip
          id="options"
          content="For further information, please check Section 215 on 'Positioning Devices' in the ADP manual."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      </Text>
      <CommentGroup
        section="section2d"
        id="1-seat-cushion"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Seat Cushion
        </Text>
        <RadioDevice
          section="section2d"
          name="seat-cushion"
          label="Seat Cushion"
          option1={{ label: "Modular", value: "sc-modular" }}
          option2={{ label: "Custom Fabricated", value: "sc-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="seat-cushion-cover"
          label="Seat Cushion Cover(s)"
          option1={{ label: "Modular", value: "scc-modular" }}
          option2={{ label: "Custom Fabricated", value: "scc-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="seat-options"
          label="Seat Option(s)"
          option1={{ label: "Modular", value: "so-modular" }}
          option2={{ label: "Custom Fabricated", value: "so-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="seat-hardware"
          label="Seat Hardware"
          option1={{ label: "Modular", value: "sh-modular" }}
          option2={{ label: "Custom Fabricated", value: "sh-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="pommel-adductors"
          label="Pommel/Adductors"
          option1={{ label: "Modular", value: "pa-modular" }}
          option2={{ label: "Custom Fabricated", value: "pa-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="pommel-hardware"
          label="Pommel Hardware"
          option1={{ label: "Custom Fabricated", value: "ph-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="2-back-support"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Back Support
        </Text>
        <RadioDevice
          section="section2d"
          name="back-support"
          label="Back Support"
          option1={{ label: "Modular", value: "bs-modular" }}
          option2={{ label: "Custom Fabricated", value: "bs-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="back-support-options"
          label="Back Support Options"
          option1={{ label: "Modular", value: "bso-modular" }}
          option2={{ label: "Custom Fabricated", value: "bso-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="back-cover"
          label="Back Cover"
          option1={{ label: "Custom Fabricated", value: "bc-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="back-hardware"
          label="Back Hardware"
          option1={{ label: "Modular", value: "bh-modular" }}
          option2={{ label: "Custom Fabricated", value: "bh-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="3-complete-assembly"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Complete Assembly
        </Text>
        <RadioDevice
          section="section2d"
          name="complete-assembly"
          label="Complete Assembly"
          option1={{ label: "Modular", value: "ca-modular" }}
          option2={{ label: "Custom Fabricated", value: "ca-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="4-headrest-neckrest"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Headrest/Neckrest
        </Text>
        <RadioDevice
          section="section2d"
          name="headrest-neckrest"
          label="Headrest/Neckrest"
          option1={{ label: "Modular", value: "hn-modular" }}
          option2={{ label: "Custom Fabricated", value: "hn-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="headrest-neckrest-options"
          label="Headrest/Neckrest Options"
          option1={{ label: "Custom Fabricated", value: "hno-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="headrest-neckrest-hardware"
          label="Headrest/Neckrest Hardware"
          option1={{ label: "Modular", value: "hnh-modular" }}
          option2={{ label: "Custom Fabricated", value: "hnh-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="5-positioning-belts"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Positioning Belts
        </Text>
        <RadioDevice
          section="section2d"
          name="positioning-belts"
          label="Positioning Belts"
          option1={{ label: "Modular", value: "pb-modular" }}
          option2={{ label: "Custom Fabricated", value: "pb-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="positioning-belt-options"
          label="Positioning Belt Options"
          option1={{ label: "Custom Fabricated", value: "pbo-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="6-arm-support"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Arm Support(s)
        </Text>
        <RadioDevice
          section="section2d"
          name="arm-support"
          label="Arm Support(s)"
          option1={{ label: "Modular", value: "as-modular" }}
          option2={{ label: "Custom Fabricated", value: "as-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="arm-support-options"
          label="Arm Support Options"
          option1={{ label: "Modular", value: "aso-modular" }}
          option2={{ label: "Custom Fabricated", value: "aso-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="arm-support-hardware"
          label="Arm Support Hardware"
          option1={{ label: "Modular", value: "ash-modular" }}
          option2={{ label: "Custom Fabricated", value: "ash-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="7-tray"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Tray
        </Text>
        <RadioDevice
          section="section2d"
          name="tray"
          label="Tray"
          option1={{ label: "Modular", value: "tray-modular" }}
          option2={{ label: "Custom Fabricated", value: "tray-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="tray-options"
          label="Tray Options"
          option1={{ label: "Modular", value: "to-modular" }}
          option2={{ label: "Custom Fabricated", value: "to-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="8-lateral-support"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Lateral Support(s)
        </Text>
        <RadioDevice
          section="section2d"
          name="lateral-support"
          label="Lateral Support(s)"
          option1={{ label: "Modular", value: "ls-modular" }}
          option2={{ label: "Custom Fabricated", value: "ls-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="lateral-support-options"
          label="Lateral Support Options"
          option1={{ label: "Custom Fabricated", value: "lso-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="lateral-support-hardware"
          label="Lateral Support Hardware"
          option1={{ label: "Custom Fabricated", value: "lsh-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="9-foot-leg-support"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text className={styles.verticalMargin} size="large" weight="bold">
          Foot/Leg Support(s)
        </Text>
        <RadioDevice
          section="section2d"
          name="foot-leg-support"
          label="Foot/Leg Support(s)"
          option1={{ label: "Modular", value: "fls-modern" }}
          option2={{ label: "Custom Fabricated", value: "fls-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="foot-leg-support-options"
          label="Foot/Leg Support Options"
          option1={{ label: "Modular", value: "flso-modular" }}
          option2={{ label: "Custom Fabricated", value: "flso-custom" }}
          readOnly={mode !== "edit"}
        />
        <RadioDevice
          section="section2d"
          name="foot-leg-support-hardware"
          label="Foot/Leg Support Hardware"
          option1={{ label: "Modular", value: "flsh-modern" }}
          option2={{ label: "Custom Fabricated", value: "flsh-custom" }}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <PageMarker page={10} total={13} />
      <CommentGroup
        section="section2d"
        id="10-reason"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2d"
          name="reason-for-application"
          label="Reason for Application"
          options={reasonOptions}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="11-replacement-devices"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2d"
          options={replacementOptions}
          name="replacement-devices"
          label="Replacement Device(s) and/or Modifications Required Due To: (check as appropriate)"
          requiredText={
            isReplacementDevicesRequired
              ? "This is a required field."
              : undefined
          }
          readOnly={mode !== "edit"}
        />
        <Modal
          title="Supporting Documents"
          isOpen={isUploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
          }}
        >
          <UploadDocumentsModal
            clientFileId={clientFile.id}
            setUploadModalOpen={setUploadModalOpen}
          />
        </Modal>
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="12-confirmation-base"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text component="span" size="large" weight="bold">
          Confirmation of Applicant’s Eligibility for a Positioning Device -
          Seating (answer required for each statement)
        </Text>
        <div className={styles.numberedRadioGroups}>
          <Text className={styles.numberBullet} weight="bold">
            1.
          </Text>
          <RadioGroup
            section="section2d"
            name="seating-components"
            description="Applicant requires the seating components to provide postural support and/or pressure
          relief during mobility. Applicant can maintain a functional posture during mobility with
          the seating components prescribed."
            options={[
              { label: "Yes", value: "sc-yes" },
              { label: "No", value: "sc-no" },
              { label: "N/A", value: "sc-na" },
            ]}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            2.
          </Text>
          <RadioGroup
            section="section2d"
            name="requires-tray"
            description="Applicant requires the tray prescribed to provide postural support during mobility and/or
          to support an ADP approved communication aid required during mobility."
            options={[
              { label: "Yes", value: "tray-yes" },
              { label: "No", value: "tray-no" },
              { label: "N/A", value: "tray-na" },
            ]}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>

      <CommentGroup
        section="section2d"
        id="13-non-adp"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2d"
          id="non-adp-funded-options-prescribed"
          label="Non ADP Funded Options Prescribed"
          maxInputs={9}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="14-setup-instructions"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2d"
          id="setup-instructions-for-vendor"
          label="Set Up Instructions for Vendor"
          maxInputs={18}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2d"
        id="15-custom-modifications"
        className={styles.margin}
        commentBoxPosition={!checkCustomModifications ? "top" : undefined}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2d"
          name="custom-modifications-required"
          label="Custom Modifications Required"
          options={[
            {
              label: "Yes",
              value: "cm-yes",
            },
            {
              label: "No",
              value: "cm-no",
            },
          ]}
          horizontal
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="custom-modifications-tooltip"
              content="If custom modifications are made on a device, a custom modification quote is required that is specifically associated with the custom work. Please include the cost for parts and labour, as well as, clinical rationale that led to this custom modification being required. "
            />
          }
        />
        {checkCustomModifications && (
          <TextArea
            section="section2d"
            id="custom-modifications-clinical-rationale"
            label="Custom Modifications Clinical Rationale"
            description="The authorizer must provide clinical rationale to support the request in the space below and attach a vendor quote that provides
          a breakdown of the cost of labour (not to exceed $40.00/hour) and parts."
            placeholder="Custom modifications required - clinical rationale"
            maxLength={1000}
            className={styles.paddingNoHorizontal}
            readOnly={mode !== "edit"}
            fieldOptions={nonSpecialCharacterOptions}
          />
        )}
      </CommentGroup>
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openPreviouslyFundedModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenPreviouslyFundedModal(false)}
        >
          <div className={styles.modalText}>
            Please provide a repair quote (Unless only seating needs to be
            replaced, then a quote is not required).
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenPreviouslyFundedModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section2d;
